import React from "react";
import Link from "next/link";

import { capitalizer } from "@geerly/shared";
import CustomAlert from "components/CustomAlert/CustomAlert.js";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";
import VisibilityIcon from "@mui/icons-material/Visibility";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableContainer,
  Collapse,
} from "@mui/material";

import { makeStyles } from "tss-react/mui";
import { getItemDetailRows } from "../dashboard/ItemDetails";
import ExpandMoreButton from "../../components/CustomButtons/ExpandMoreButton";
import ItemOptions from "../dashboard/ItemOptions";
import { handleAddWatchedProduct } from "../dashboard/AddWatchedProductPopUp";
import { useFullUser } from "../../auth/useUser";
import { getSupportsShoes } from "../../modules/utils";
import { ANY_COLOUR_NAME } from "../../settings/global-website-config";
import { PriceRange } from "../../components/PriceComparison/PriceComparisonModule";
import { getSizeDeals } from "../../modules/products/useSizeAndSex";

const useStyles = makeStyles()({});

const ProductWatchingDetails = (props) => {
  const { type } = props;
  const { t } = useTranslation();

  return (
    <Box sx={{ ml: 1 }}>
      <VisibilityIcon sx={{ fontSize: "0.9rem", verticalAlign: "middle" }} />{" "}
      {t("product:watchingProduct", { type })}
    </Box>
  );
};

const ProductInUseDetails = (props) => {
  const { user, userProduct, supportsShoes, type, provider } = props;
  const { t } = useTranslation();
  const { items } = userProduct;

  const [viewActivitiesOpen, setViewActivitiesOpen] = React.useState();

  const { classes } = useStyles();

  const hasDescription = userProduct.items.some(
    (i) => i.itemDetails.description
  );
  const rows = items.map((item) => {
    const columns = getItemDetailRows(
      item,
      userProduct,
      user,
      viewActivitiesOpen,
      setViewActivitiesOpen,
      classes,
      true,
      hasDescription
    );
    return columns;
  });

  return (
    <Box sx={{ ml: 1 }}>
      {t("product:usingProduct", {
        provider,
        type,
      })}

      {supportsShoes && (
        <TableContainer sx={{ width: "100%", my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {rows[0].map((column) => (
                  <TableCell key={`header-${column.name}`}>
                    {column.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& > :last-child": {
                  "& > td": { borderBottom: "none" },
                },
              }}
            >
              {rows.map((columns, index) => {
                return (
                  <TableRow key={`column-${index}`}>
                    {columns.map((column) => (
                      <TableCell key={`column-${index}-${column.name}`}>
                        {column.value}
                        {column.extraContent}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
const getFromPrice = (databaseId, alternativeDeals, typeKey) => {
  if (!alternativeDeals) {
    return;
  }
  const sizes = Object.keys(alternativeDeals);
  const matchedDeals = [];
  for (const size of sizes) {
    if (alternativeDeals[size][typeKey]) {
      for (const entry of alternativeDeals[size][typeKey]) {
        if ((entry.databaseId || entry.model?.id) === databaseId) {
          matchedDeals.push(entry);
        }
      }
    }
  }
  if (matchedDeals.length) {
    return (
      <PriceRange
        deals={{
          lowPrice: matchedDeals.reduce((prev, curr) =>
            prev.price < curr.price ? prev : curr
          ).displayPrice,
          highPrice: matchedDeals.reduce((prev, curr) =>
            prev.price > curr.price ? prev : curr
          ).displayPrice,
        }}
      />
    );
  }
};

export const getDisplayPrice = (product, state, deals, typeKey) => {
  const alternativeDeals = state.size?.length
    ? getSizeDeals(state, deals?.colours?.[ANY_COLOUR_NAME])
    : deals?.colours?.[ANY_COLOUR_NAME];

  if (!alternativeDeals) {
    return;
  }
  const displayPrice = state.size?.length
    ? alternativeDeals?.[typeKey]?.find(
        (p) => p.databaseId === product.databaseId
      )?.displayPrice
    : getFromPrice(product.databaseId, alternativeDeals, typeKey);

  if (!displayPrice) {
    return null;
  }
  return `(${displayPrice})`;
};

const UserRelatedProductsDetails = (props) => {
  const {
    usageStatus,
    userProducts,
    family,
    dealsState,
    state,
    typeKey,
    dealsTypeKey,
    type,
    provider,
  } = props;

  return (
    <Box sx={{ ml: 1 }}>
      <Trans
        i18nKey={
          usageStatus === "using" || usageStatus === "watching"
            ? `product:userProducts.${typeKey}Also`
            : `product:userProducts.${typeKey}`
        }
        components={{ link: <Link href={family?.uri} /> }}
        values={{
          family: family?.termName,
          count: userProducts.length,
          type,
          provider,
        }}
      />
      <List>
        {userProducts.map((product) => {
          if (product.product) {
            // Related family products have a different structure :()
            product = product.product;
          }
          const typeKeyToUse = dealsTypeKey || typeKey + "Deals";
          const deals = dealsState?.sexDeals?.[product.databaseId]?.deals;

          const displayPrice = getDisplayPrice(
            product,
            state,
            deals,
            typeKeyToUse
          );
          return (
            <ListItem sx={{ p: 0 }} key={`userFamilyProduct-${product.uri}`}>
              <Link href={product.uri}>
                {product.noBrandTitle || product.title}
              </Link>
              {displayPrice && (
                <Box component="span" sx={{ ml: 0.5 }}>
                  {displayPrice}
                </Box>
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
UserRelatedProductsDetails.defaultProps = { typeKey: "familyProductDeals" };
const ProductInUseSection = (props) => {
  const {
    user,
    usageStatus,
    userProduct,
    product,
    userFamilyProducts,
    userSpecialVersions,
    userBaseModels,
    viewSettingsOpen,
    setViewSettingsOpen,
    family,
    dealsState,
    state,
    type,
    localeCurrency,
    ...rest
  } = props;

  const { t } = useTranslation();

  const [_, { handleUpdateUser }] = useFullUser();

  const supportsShoes = getSupportsShoes(product?.category.node);
  const provider = capitalizer(user?.provider);

  const relatedProductProps = {
    family,
    usageStatus,
    dealsState,
    product,
    state,
    type,
    provider,
    dealsTypeKey: props.type === "family" ? "all" : null,
  };

  return (
    <CustomAlert
      sx={{ mb: { sm: 2 } }}
      fullWidth
      variant="outlined"
      dense={false}
    >
      {usageStatus === "using" ? (
        <ProductInUseDetails
          user={user}
          userProduct={userProduct}
          supportsShoes={supportsShoes}
          type={type}
          provider={provider}
          {...rest}
        />
      ) : (
        usageStatus === "watching" && (
          <ProductWatchingDetails user={user} type={type} {...rest} />
        )
      )}
      {userFamilyProducts?.length ? (
        <UserRelatedProductsDetails
          {...relatedProductProps}
          userProducts={userFamilyProducts}
          typeKey={"familyProduct"}
        />
      ) : null}
      {userSpecialVersions?.length ? (
        <UserRelatedProductsDetails
          {...relatedProductProps}
          userProducts={userSpecialVersions}
          typeKey={"specialVersion"}
        />
      ) : null}
      {userBaseModels?.length ? (
        <UserRelatedProductsDetails
          {...relatedProductProps}
          userProducts={userBaseModels}
          typeKey={"baseModel"}
        />
      ) : null}
      {(usageStatus === "using" || usageStatus === "watching") && (
        <Box sx={{ mt: 1 }}>
          <ExpandMoreButton
            open={viewSettingsOpen}
            setOpen={setViewSettingsOpen}
          >
            {t("product:alertsSettings")}
          </ExpandMoreButton>
          <Button variant="text">
            <Link
              href={`/dashboard?target=${product?.category.node.slug}#tabs`}
            >
              {" "}
              {t("nav:dashboard")}
            </Link>
          </Button>
          {usageStatus === "watching" && (
            <Button
              variant="text"
              color="primary"
              onClick={(e) =>
                handleAddWatchedProduct(
                  userProduct.product.databaseId,
                  { product: e },
                  user,
                  handleUpdateUser,
                  "unwatch"
                )
              }
            >
              {t("product:unwatch")}
            </Button>
          )}
          <Collapse
            in={props.viewSettingsOpen}
            timeout="auto"
            unmountOnExit
            sx={{ px: 2 }}
          >
            <ItemOptions
              fixedMinWidth
              productEntry={userProduct}
              athlete={user}
              localeCurrency={localeCurrency}
              supportsShoes={supportsShoes}
            />
          </Collapse>
        </Box>
      )}
    </CustomAlert>
  );
};

export default ProductInUseSection;
